import { useMeterReadings } from "@features/bx/BulkBillGeneration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useToast } from "@shared/hooks/use-toast";
import { TableAction, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  ExternalLink,
  Info,
} from "lucide-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { MissingReadingFilters } from "./Filters/MissingReadingFilters";

// Interface for Pending Validation data
interface PendingValidation {
  id: string | number;
  consumerNumber: string;
  consumerName: string;
  meterNo: string;
  meterType: string;
  service: string;
  lastReading: number | string;
  lastReadingDate: string;
  validatorId?: string | number;
  validatorName?: string;
}

interface MeterReadingFiltersType {
  meterType: string;
  service: string;
  page?: number;
  limit?: number;
  veeStatus?: string;
}

export const PendingValidationTable: React.FC = () => {
  const { toast } = useToast();
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = PAGE_SIZE;
  const [searchTerm, setSearchTerm] = useState("");

  const pageSize = PAGE_SIZE;
  const { id, cycleId, action } = useParams();

  const [filters, setFilters] = useState<MeterReadingFiltersType>({
    meterType: "all",
    service: "all",
    page: currentPage,
    limit: pageSize,
    veeStatus: "PENDING",
  });

  const formatFiltersForHook = (
    filters: MeterReadingFiltersType
  ): { page: number; limit: number; search?: string; [key: string]: any } => {
    const hookFilters: {
      page: number;
      limit: number;
      search?: string;
      [key: string]: any;
    } = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    // Add search term
    if (searchTerm.trim()) {
      hookFilters.search = searchTerm;
    }

    if (filters.veeStatus && filters.veeStatus !== "all") {
      hookFilters.veeStatus = filters.veeStatus;
    }
    // Add meter type filter
    if (filters.meterType && filters.meterType !== "all") {
      hookFilters.meterType = filters.meterType;
    }

    // Add service filter
    if (filters.service && filters.service !== "all") {
      hookFilters.service = filters.service;
    }

    if (filters.veeStatus) {
      hookFilters.veeStatus = filters.veeStatus;
    }

    return hookFilters;
  };

  const { data: meterReadings, isLoading } = useMeterReadings(cycleId as string, id as string, formatFiltersForHook(filters));

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle individual Validate button click
  const handleValidateRow = (row: PendingValidation) => {
    console.log("Validate Row - ID:", row.id);
  };

  // Open alert modal
  const handleOpenAlertModal = () => {
    setIsAlertModalOpen(true);
  };

  // Close alert modal
  const handleCloseAlertModal = () => {
    setIsAlertModalOpen(false);
  };

  // Handle send alerts
  const handleSendAlerts = () => {
    // Extract unique validator IDs from data

    const notificationMessage = `Validation Request -  has requested you to complete meter reading validation as bill creation for the period  is pending due to non-validated readings.`;

    // Close modal
    handleCloseAlertModal();

    // Show success toast
    toast({
      title: "Alerts Sent Successfully",
      description: `Notification sent to validator(s) with pending validations.`,
      variant: "default",
    });
  };

  // Handle Go to Validation button
  const handleGoToValidation = () => {
    window.open("/mx/meter-validation", "_blank");
  };

  // Define table columns
  const columns: TableColumn<PendingValidation>[] = [
    {
      key: "consumerNumber",
      header: "Consumer Number",
    },
    {
      key: "consumerName",
      header: "Consumer Name",
    },
    {
      key: "meterNo",
      header: "Resource ID",
    },
    {
      key: "meterType",
      header: "Resource Type",
    },
    {
      key: "service",
      header: "Service",
    },
    {
      key: "lastReading",
      header: "Last Reading",
    },
    {
      key: "lastReadingDate",
      header: "Last Reading Date",
    },
  ];

  // Define row actions
  const actions: TableAction<PendingValidation>[] = [
    {
      label: "Validate",
      onClick: handleValidateRow,
      variant: "outline",
    },
  ];

  const totalItems = meterReadings?.count || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Empty state component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <CheckCircle2 className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        No Pending Validations
      </h3>
      <p className="text-sm text-gray-600">
        All readings have been validated successfully.
      </p>
    </div>
  );

  const handleFiltersChange = (newFilters: MeterReadingFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      meterType: "all",
      service: "all",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Alert Banner */}
      {meterReadings?.results?.length > 0 && (
        <div className="flex items-start justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start space-x-3 flex-1">
            <AlertTriangle
              className="text-orange-600 mt-0.5 flex-shrink-0"
              size={20}
            />
            <div>
              <h3 className="font-semibold text-orange-900 mb-1">
                Unvalidated Readings Detected
              </h3>
              <p className="text-sm text-orange-800 mb-2">
                The readings below have not been validated and may cause billing
                discrepancies. Please review and validate all readings before
                proceeding with bill generation.
              </p>
              <p className="text-sm text-orange-700 font-medium">
                Action Required: Validators must review and approve these
                readings to ensure accurate billing.
              </p>
            </div>
          </div>
          <Button
            onClick={handleOpenAlertModal}
            className="ml-4 flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white flex-shrink-0"
          >
            <Bell className="h-4 w-4" />
            Send Alert to Validator
          </Button>
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Pending Validation
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Readings that require manual validation
            </p>
          </div>

          {/* Go to Validation Button */}
          {meterReadings?.results?.length > 0 && (
            <Button
              onClick={handleGoToValidation}
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Go to Validation
            </Button>
          )}
        </div>

      
          <GenericTable
            data={meterReadings?.results || []}
            columns={columns}
            rowKey="id"
            loading={false}
            selectable={false}
            actions={actions}
            actionsType="buttons"
            onSearch={handleSearch}
            filterPanel={
              <MissingReadingFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApplyFilters={handleFiltersChange}
                onClearFilters={clearFilters}
              />
            }
            pagination={{
              currentPage,
              totalPages,
              totalItems,
              itemsPerPage,
              startItem,
              endItem,
            }}
            onPageChange={handlePageChange}
            emptyMessage="No pending validations found"
          />
      </div>

      {/* Alert Modal */}
      <Dialog open={isAlertModalOpen} onOpenChange={setIsAlertModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <DialogTitle className="text-lg font-semibold">
                Send Validation Alert
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-gray-600">
              Confirm notification to validators
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Info Box */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900 mb-1">
                  Notification will be sent to:
                </p>
                <p className="text-sm text-blue-800">
                  All validators who have pending validations assigned to them
                  for this billing cycle.
                </p>
              </div>
            </div>

            {/* Additional Info */}
            <p className="text-sm text-gray-600">
              This will send an immediate notification to remind validators to
              complete their pending validations. Validators will receive alerts
              via their configured notification channels.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseAlertModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSendAlerts}
              className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
            >
              <Bell className="h-4 w-4" />
              Send Alerts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
